import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  checkConnectionHealth,
  createTrigger,
  createTriggerConnection,
  createTriggerSubscription,
  deleteTrigger,
  deleteTriggerConnection,
  deleteTriggerSubscription,
  initiateConnectionAuth,
  setTriggerEnabled,
  testFireTrigger,
} from '@/services/triggers/triggers';
import {
  CreateConnectionInput,
  CreateSubscriptionInput,
  CreateTriggerInput,
} from '@/services/triggers/types';
import { triggerConnectionsQueryKey } from '@/hooks/queries/triggers/use-list-trigger-connections';
import { triggersQueryKey } from '@/hooks/queries/triggers/use-list-triggers';
import { triggerSubscriptionsQueryKey } from '@/hooks/queries/triggers/use-list-trigger-subscriptions';

const useInvalidateConnections = (orgId?: string) => {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({
      queryKey: triggerConnectionsQueryKey(orgId),
    });
};

const useInvalidateTriggers = (orgId?: string) => {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({ queryKey: triggersQueryKey(orgId) });
};

/**
 * Refresh both lists after a binding changes.
 *
 * Workflow-scoped trigger lists are filtered server-side by binding, so
 * attaching and detaching both add and remove rows from them — not just from the
 * subscriptions list. Shared by create and delete so the two cannot drift.
 */
const useInvalidateBindings = (orgId?: string) => {
  const queryClient = useQueryClient();
  return async (triggerId: string) => {
    await queryClient.invalidateQueries({
      queryKey: triggerSubscriptionsQueryKey(orgId, triggerId),
    });
    await queryClient.invalidateQueries({ queryKey: triggersQueryKey(orgId) });
  };
};

export const useCreateTriggerConnection = (orgId?: string) => {
  const invalidate = useInvalidateConnections(orgId);
  return useMutation({
    mutationFn: (input: CreateConnectionInput) =>
      createTriggerConnection(orgId!, input),
    onSuccess: invalidate,
  });
};

export const useInitiateConnectionAuth = (orgId?: string) => {
  const invalidate = useInvalidateConnections(orgId);
  return useMutation({
    mutationFn: ({
      connectionId,
      popup = false,
    }: {
      connectionId: string;
      popup?: boolean;
    }) => initiateConnectionAuth(orgId!, connectionId, popup),
    onSuccess: invalidate,
  });
};

export const useCheckConnectionHealth = (orgId?: string) => {
  const invalidate = useInvalidateConnections(orgId);
  return useMutation({
    mutationFn: (connectionId: string) =>
      checkConnectionHealth(orgId!, connectionId),
    onSuccess: invalidate,
  });
};

export const useDeleteTriggerConnection = (orgId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (connectionId: string) =>
      deleteTriggerConnection(orgId!, connectionId),
    // Deleting a connection cascades to its triggers, so both lists are stale.
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: triggerConnectionsQueryKey(orgId),
      });
      await queryClient.invalidateQueries({
        queryKey: triggersQueryKey(orgId),
      });
    },
  });
};

export const useCreateTrigger = (orgId?: string) => {
  const invalidate = useInvalidateTriggers(orgId);
  return useMutation({
    mutationFn: (input: CreateTriggerInput) => createTrigger(orgId!, input),
    onSuccess: invalidate,
  });
};

export const useSetTriggerEnabled = (orgId?: string) => {
  const invalidate = useInvalidateTriggers(orgId);
  return useMutation({
    mutationFn: ({
      triggerId,
      enabled,
    }: {
      triggerId: string;
      enabled: boolean;
    }) => setTriggerEnabled(orgId!, triggerId, enabled),
    onSuccess: invalidate,
  });
};

export const useDeleteTrigger = (orgId?: string) => {
  const invalidate = useInvalidateTriggers(orgId);
  return useMutation({
    mutationFn: (triggerId: string) => deleteTrigger(orgId!, triggerId),
    onSuccess: invalidate,
  });
};

export const useTestFireTrigger = (orgId?: string) => {
  const invalidate = useInvalidateTriggers(orgId);
  return useMutation({
    mutationFn: (triggerId: string) => testFireTrigger(orgId!, triggerId),
    onSuccess: invalidate,
  });
};

export const useCreateTriggerSubscription = (orgId?: string) => {
  const invalidate = useInvalidateBindings(orgId);
  return useMutation({
    mutationFn: ({
      triggerId,
      input,
    }: {
      triggerId: string;
      input: CreateSubscriptionInput;
    }) => createTriggerSubscription(orgId!, triggerId, input),
    onSuccess: (_data, { triggerId }) => invalidate(triggerId),
  });
};

export const useDeleteTriggerSubscription = (orgId?: string) => {
  const invalidate = useInvalidateBindings(orgId);
  return useMutation({
    mutationFn: ({
      triggerId,
      subscriptionId,
    }: {
      triggerId: string;
      subscriptionId: string;
    }) => deleteTriggerSubscription(orgId!, triggerId, subscriptionId),
    onSuccess: (_data, { triggerId }) => invalidate(triggerId),
  });
};
